import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, UserRound } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import api, { formatApiErrorDetail } from "../lib/api";
import { signOutAndGoHome, signOutEverywhereAndGoToSignIn } from "../lib/signOut";
import { useAuth } from "../context/AuthContext";
import CoachProfileCard from "./CoachProfileCard";
import PlayerProfile from "../pages/PlayerProfile";

const DISPLAY_NAME_MAX = 60;
/**
 * Issue #456. The same 40 the API's `MOBILE_PHONE_MAX` and the
 * `users_mobile_phone_length_check` constraint use — carried here for the
 * client-side check exactly as `DISPLAY_NAME_MAX` is, so the field says
 * "too long" before a round trip does.
 */
const MOBILE_PHONE_MAX = 40;

/**
 * "Account & profile" — the head of `/settings`, and ONE area on purpose.
 *
 * Issue #456, decision comment of 2026-08-27 (Andreas): "We want one
 * section in settings, both profile and account as one area." Until then
 * Settings opened with two adjacent cards — Account (sign-in identity,
 * sign-out) and the role's profile editor (the identity other people see)
 * — with a documented rationale for keeping them separate-but-adjacent.
 * That rationale is superseded: this card holds everything both cards
 * held, under one header, for every role.
 *
 * NOT a collapsible `SectionCard`, deliberately. The #456/#459 folding
 * pass left Account and the profile editors unfolded because they are the
 * page's head — the answer to "who am I here?", not an accumulating log —
 * and merging them does not change that. It also keeps the
 * `#player-profile` anchor honest: Dashboard's "Complete profile" button
 * deep-links here (`/settings#player-profile`, honoured by ScrollToTop),
 * and a fold that arrives closed would land that player on a shut header.
 * So: always open, no `summary` obligation, and the collapsible call-site
 * table in identityForm.test.js is untouched.
 *
 * THE HALVES KEEP THEIR OWN COMPONENTS. `PlayerProfile` is also step 1 of
 * the first-run walkthrough and `CoachProfileCard` owns the photo's own
 * save path (#478) — this card hosts them, it does not absorb them. Both
 * take a narrow `frameless` prop (the same pattern as `showHeading` —
 * PlayerProfile.jsx explains why narrow props beat modes) so they drop
 * their own bordered chrome inside this card: a bordered card nested in a
 * bordered card reads as a rendering mistake, which is the exact sentence
 * the old `<section>` wrapper carried.
 *
 * SIGN OUT STAYS LAST, below its own separator — the foot of the identity
 * it ends. #262 put it here because Settings is the one page every
 * viewport can reach; the merge moves it down the page but not out of
 * "the obvious place".
 *
 * Role shape: player gets Account + player profile; coach gets Account +
 * coaching profile; an admin has neither profile (a platform role, not a
 * cricket identity) and gets the account fields and sign-out alone.
 *
 * MOBILE NUMBER (#456, Andreas's decision of 2026-09-07) SITS IN THE
 * ACCOUNT HALF, ABOVE THE SEPARATOR, AND THAT PLACEMENT IS THE POINT. The
 * profile half below is the identity OTHER PEOPLE SEE — a coach reads it,
 * and #288 may one day list it. The number is private to the account
 * holder: it is on `/auth/me` and on no other read in the API, not the
 * coach's view of their player, not the admin roster. Putting it beside
 * "Display name" and "Email" — the sign-in identity, the fields nobody else
 * reads — is what keeps that true on the screen as well as on the wire, and
 * the helper text under the input says it in words for the person typing
 * it. If this field ever migrates below the separator, the visibility rule
 * has to be re-decided first: this platform has minors on it.
 */
export default function AccountProfileCard() {
  const { user, updateUser, logout, logoutEverywhere } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [nameError, setNameError] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Keep the field in sync if `user` loads/changes after mount (e.g. the
  // initial /auth/me probe resolves after this component has already
  // rendered with `user` still null).
  useEffect(() => { setDisplayName(user?.display_name || ""); }, [user?.display_name]);

  const dirtyName = displayName !== (user?.display_name || "");

  // #456. Its own field, its own dirty flag and its own save, mirroring the
  // display name directly above — because `PUT /auth/me` treats an absent
  // key as "leave it alone", so each button sends only what it edits and
  // neither can clear the other's value.
  const [mobilePhone, setMobilePhone] = useState(user?.mobile_phone || "");
  const [phoneError, setPhoneError] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  useEffect(() => { setMobilePhone(user?.mobile_phone || ""); }, [user?.mobile_phone]);
  const dirtyPhone = mobilePhone !== (user?.mobile_phone || "");

  const saveMobilePhone = async () => {
    const trimmed = mobilePhone.trim();
    if (trimmed.length > MOBILE_PHONE_MAX) {
      setPhoneError(`Mobile number must be ${MOBILE_PHONE_MAX} characters or fewer.`);
      return;
    }
    setPhoneError("");
    setSavingPhone(true);
    try {
      // Sent EXACTLY as typed, bar the trim — no reformatting here and none
      // on the server (api/src/lib/mobilePhone.ts says why). `""` is the
      // clear instruction; the API maps it to null.
      const { data } = await api.put("/auth/me", { mobile_phone: trimmed });
      updateUser(data);
      setMobilePhone(data.mobile_phone || "");
      toast.success(trimmed ? "Mobile number updated" : "Mobile number removed");
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || "Save failed";
      setPhoneError(message);
      toast.error(message);
    }
    setSavingPhone(false);
  };

  const saveDisplayName = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setNameError("Display name is required.");
      return;
    }
    if (trimmed.length > DISPLAY_NAME_MAX) {
      setNameError(`Display name must be ${DISPLAY_NAME_MAX} characters or fewer.`);
      return;
    }
    setNameError("");
    setSavingName(true);
    try {
      const { data } = await api.put("/auth/me", { display_name: trimmed });
      updateUser(data);
      setDisplayName(data.display_name);
      toast.success("Display name updated");
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || "Save failed";
      setNameError(message);
      toast.error(message);
    }
    setSavingName(false);
  };

  // Issue #262. Deliberately the SAME call the account menu and the sidebar
  // make — see lib/signOut.js for what it does and why. Three controls that
  // end the same session must not end it three slightly different ways.
  const signOut = async () => {
    setSigningOut(true);
    const result = await signOutAndGoHome(logout);
    // #326: a failed sign-out does not navigate, so the button must come
    // back — see the same three lines in AccountMenu.jsx.
    if (result?.ok === false) setSigningOut(false);
  };

  // Issue #515: "Sign out everywhere" — revokes every session on every
  // device, this one included, then lands on /login (lib/signOut.js says
  // why /login and not "/"). TWO-STEP on purpose: the first press only
  // arms the row (confirmingEverywhere), and the destructive POST happens
  // on a second, differently-worded press. This button signs the user out
  // of devices they cannot see from here, sits one row under the ordinary
  // sign-out, and a slipped tap must not do silently more than the button
  // above it does loudly. An inline arm-and-confirm rather than an
  // AlertDialog keeps the confirmation, its consequences, and the cancel
  // in the exact spot the user is already reading.
  const [confirmingEverywhere, setConfirmingEverywhere] = useState(false);
  const [signingOutEverywhere, setSigningOutEverywhere] = useState(false);
  const signOutEverywhere = async () => {
    setSigningOutEverywhere(true);
    const result = await signOutEverywhereAndGoToSignIn(logoutEverywhere);
    // #326's rule, same as signOut above: a failed revoke does not
    // navigate, so the controls must come back — disarmed, so a retry
    // re-reads the warning rather than inheriting a stale confirmation.
    if (result?.ok === false) {
      setSigningOutEverywhere(false);
      setConfirmingEverywhere(false);
    }
  };

  const role = user?.role;
  const title =
    role === "player" ? "Account & profile"
    : role === "coach" ? "Account & coaching profile"
    : "Account";
  const desc =
    role === "player" ? "Your sign-in details and the cricket profile other people see."
    : role === "coach" ? "Your sign-in details and how you introduce yourself to players."
    : null;

  return (
    <Card className="border-slate-200" data-testid="settings-account-profile-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
            <UserRound size={16} color="#0B2E59" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg text-slate-900">{title}</h2>
            {desc && <p className="text-sm text-slate-500">{desc}</p>}
          </div>
        </div>

        <div>
          <Label htmlFor="settings-display-name">Display name</Label>
          <Input
            id="settings-display-name"
            data-testid="settings-display-name"
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); if (nameError) setNameError(""); }}
            maxLength={200}
            className="mt-1.5 h-11"
            aria-invalid={nameError ? "true" : undefined}
            aria-describedby={nameError ? "settings-display-name-error" : undefined}
          />
          {nameError && (
            <p id="settings-display-name-error" className="text-sm text-red-600 mt-1" data-testid="settings-display-name-error">
              {nameError}
            </p>
          )}
          <Button
            onClick={saveDisplayName}
            disabled={savingName || !dirtyName}
            className="mt-2 h-9"
            style={{ background: "#0B2E59" }}
            data-testid="settings-save-display-name"
          >
            {savingName ? "Saving…" : "Save name"}
          </Button>
        </div>
        {/* #456 — see the docstring for why this lives in the account half
            rather than the profile half. `type="tel"` for the mobile
            keypad; deliberately NO pattern, no input mask and no
            auto-formatting: "+64 21 555 0000", "021 555 0000" and
            "(021) 555-0000" are all things a club parent types and all of
            them must survive. `inputMode="tel"` rather than `numeric` so
            "+" stays reachable. */}
        <div>
          <Label htmlFor="settings-mobile-phone">Mobile number</Label>
          <Input
            id="settings-mobile-phone"
            data-testid="settings-mobile-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+64 21 555 0000"
            value={mobilePhone}
            onChange={(e) => { setMobilePhone(e.target.value); if (phoneError) setPhoneError(""); }}
            maxLength={MOBILE_PHONE_MAX}
            className="mt-1.5 h-11"
            aria-invalid={phoneError ? "true" : undefined}
            aria-describedby={phoneError ? "settings-mobile-phone-error" : "settings-mobile-phone-help"}
          />
          <p id="settings-mobile-phone-help" className="text-sm text-slate-500 mt-1">
            Start with your country code, e.g. +64 21 555 0000.
            Only you can see this. It isn't shown to your coach or to anyone else, and we don't
            send text messages — to reach a coach, use Coach access below.
          </p>
          {phoneError && (
            <p id="settings-mobile-phone-error" className="text-sm text-red-600 mt-1" data-testid="settings-mobile-phone-error">
              {phoneError}
            </p>
          )}
          <Button
            onClick={saveMobilePhone}
            disabled={savingPhone || !dirtyPhone}
            className="mt-2 h-9"
            style={{ background: "#0B2E59" }}
            data-testid="settings-save-mobile-phone"
          >
            {savingPhone ? "Saving…" : "Save number"}
          </Button>
        </div>
        <div><Label>Email</Label><Input value={user?.email || ""} readOnly className="mt-1.5 h-11 bg-slate-50" /></div>
        <div><Label>Role</Label><Input value={user?.role || ""} readOnly className="mt-1.5 h-11 bg-slate-50 capitalize" /></div>

        {/* #338 folded the PlayerProfile editor into this page; #456's
            2026-08-27 decision folded it into this CARD. Still the
            COMPONENT the walkthrough renders at step 1, not a copy of its
            form (PlayerProfile.jsx). `showHeading={false}` because
            Settings owns the page's one <h1> and this card owns the
            section header; `frameless` because the host card provides the
            border the component would otherwise draw twice.

            The id is a real anchor — Dashboard's "Complete profile"
            button links to `/settings#player-profile` and ScrollToTop
            honours a hash whose element exists — so it lands ON the
            profile half of this card rather than at the top of the page.
            `scroll-mt-20` keeps it clear of the sticky topbar. The
            testid stays on this player-only wrapper (not the card, which
            every role gets) so the journey suite can keep asserting a
            coach and an admin see no player form. */}
        {role === "player" && (
          <>
            <Separator />
            <div
              id="player-profile"
              data-testid="settings-player-profile"
              className="space-y-4 scroll-mt-20"
            >
              <p className="text-sm text-slate-500">
                Your cricket profile — name, club, batting and bowling style, playing role.
              </p>
              <PlayerProfile showHeading={false} frameless />
            </div>
          </>
        )}

        {/* Issue #290's coaching profile, hosted frameless for the same
            reason as the player half. Gated on the coach ROLE, not a
            coach type — #203 is open; see CoachProfileCard.jsx for why
            that gate stays cheap to change. The card keeps its own
            testid, photo controls and save path; only its chrome moved. */}
        {role === "coach" && (
          <>
            <Separator />
            <CoachProfileCard frameless />
          </>
        )}

        <Separator />

        {/* Issue #262. Settings is in PLAYER_NAV, COACH_NAV and ADMIN_NAV,
            so it is the one page reachable at every viewport from the
            mobile nav strip — which is why #262 asks for a sign-out here
            as well as in the topbar. The topbar account menu (#318) is
            the primary control; this is the one a user finds by looking
            for it in the obvious place. Both end the same session and
            both land on "/", the public front door a signed-in device
            could not otherwise get back to. */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900">Sign out</div>
            <div className="text-sm text-slate-500">
              Ends this session on this device. You'll go back to the CricketCompass front page.
            </div>
          </div>
          <Button
            onClick={signOut}
            disabled={signingOut}
            variant="outline"
            className="h-10 shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid="settings-signout"
          >
            <LogOut size={16} className="mr-2" />
            {signingOut ? "Signing out…" : "Sign out"}
          </Button>
        </div>

        {/* Issue #515 — the everywhere-revoke, directly under the ordinary
            sign-out it escalates. This control is the counterpart of the
            login form's "keep me signed in for 30 days" opt-in: it is what
            bounds a long-lived session on a lost or shared device, so it
            has to be findable in the same obvious place sign-out is. The
            two-step arm/confirm is argued on the handler above. */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900">Sign out everywhere</div>
            <div className="text-sm text-slate-500">
              Ends your session on every device — including any where you ticked "keep me signed
              in", and this one. Use it if a device is lost, shared, or you're not sure who's
              signed in. You'll sign in again afterwards.
            </div>
          </div>
          {confirmingEverywhere ? (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                onClick={() => setConfirmingEverywhere(false)}
                disabled={signingOutEverywhere}
                variant="outline"
                className="h-10"
                data-testid="settings-signout-everywhere-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={signOutEverywhere}
                disabled={signingOutEverywhere}
                variant="outline"
                className="h-10 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="settings-signout-everywhere-confirm"
              >
                {signingOutEverywhere ? "Signing out…" : "Yes, sign out every device"}
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setConfirmingEverywhere(true)}
              variant="outline"
              className="h-10 shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="settings-signout-everywhere"
            >
              <LogOut size={16} className="mr-2" />
              Sign out everywhere
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
